import { getMergedProductData } from '@kissui/page-builder-sections/src/cross-sell-natural/dataTransform'

import {
    CapsuleFeaturesInputType,
    CapsuleFeaturesOutputType,
    ProductPropsType,
    AddToCartPropsType,
    SkuType,
    ProductNewPropsType,
    MergedProductType,
    AddToCardPropertiesType,
    QuickViewPropsType,
    VARIATION_PLP,
    VARIATION_CROSS_SELL
} from './shared-quick-view-types'

export const getCapsuleFeatures = ({
    acidity = null,
    bitterness = null,
    body = null,
    intensity = null,
    roastLevel = null
}: CapsuleFeaturesInputType = {}): CapsuleFeaturesOutputType => ({
    acidity: { value: acidity },
    bitterness: { value: bitterness },
    body: { value: body },
    intensity: { value: intensity },
    roast: { value: roastLevel }
})

export const getSkuQuickViewData = async (
    product: ProductPropsType,
    popinId: string,
    skuData: SkuType,
    componentType: string
) => {
    const { tracking_position, tracking_list } = skuData

    const handlePLPVariation = () => {
        if (!product) return
        let quickViewElement = document.createElement('nb-sku-quick-view')
        quickViewElement.setAttribute(
            'data',
            JSON.stringify(getSkuQuickViewProp(popinId, product, skuData, componentType))
        )
        quickViewElement.setAttribute('id', popinId)
        quickViewElement.setAttribute('tracking_no_impression', 'true')
        quickViewElement.setAttribute('tracking_position', tracking_position)
        quickViewElement.setAttribute('tracking_list', tracking_list)
        quickViewElement.setAttribute('campaign_position', skuData.campaign.position)
        quickViewElement.setAttribute('listname_text', skuData?.copywriting?.heading)
        document.body.appendChild(quickViewElement)
    }

    const handleCrossSellVariation = () => {
        const { sku_quick_view } = skuData.copywriting
        if (!sku_quick_view.more_info) return ''

        const quickViewElement = document.querySelector(
            `nb-sku-quick-view > nb-popin[id="${popinId}"]`
        )?.parentElement

        if (quickViewElement) quickViewElement.remove()

        let newQuickViewElement = document.createElement('nb-sku-quick-view')
        newQuickViewElement.setAttribute(
            'data',
            JSON.stringify(getSkuQuickViewProp(popinId, product, skuData, componentType))
        )
        newQuickViewElement.setAttribute('tracking_no_impression', 'true')
        newQuickViewElement.setAttribute('tracking_position', tracking_position)
        newQuickViewElement.setAttribute('tracking_list', tracking_list)
        newQuickViewElement.setAttribute('campaign_position', skuData.campaign.position)
        newQuickViewElement.setAttribute('listname_text', skuData?.copywriting?.heading)
        document.body.appendChild(newQuickViewElement)
    }

    const variationHandlers: { [key: string]: () => void | string } = {
        [VARIATION_PLP]: handlePLPVariation,
        [VARIATION_CROSS_SELL]: handleCrossSellVariation
    }

    const handleVariation = variationHandlers[componentType]
    if (handleVariation) {
        handleVariation()
    }
}

export const getSkuQuickViewProp = (
    popinId: string,
    product: ProductPropsType,
    skuData: SkuType,
    componentType: string
) => {
    let mergedProductData = getMergedProductDataForComponentType(product, skuData, componentType)
    let addToCartProps = getAddToCartProperties(componentType, skuData, mergedProductData)

    return isVariationOnPLP(componentType)
        ? getPlpQuickViewProps(popinId, skuData, mergedProductData, addToCartProps)
        : getCrossSellQuickViewProps(popinId, product, skuData, mergedProductData, addToCartProps)
}

const getMergedProductDataForComponentType = (
    product: ProductPropsType,
    skuData: SkuType,
    componentType: string
) => {
    switch (componentType) {
        case VARIATION_PLP:
            return {
                ...skuData,
                ...product,
                ...getCapsuleFeatures(product?.capsuleProperties)
            }
        case VARIATION_CROSS_SELL: {
            const matchedProduct =
                skuData.products?.find(
                    productPageBuilder => productPageBuilder.sku === product?.legacyId
                ) || {}
            return getMergedProductData(
                product,
                matchedProduct,
                skuData,
                skuData?.priceFormatter,
                skuData?.currency
            )
        }
        default:
            return {}
    }
}

export const getAddToCartProperties = (
    componentType: string,
    skuData: SkuType,
    mergedProductData: MergedProductType
): AddToCartPropsType => {
    const data: AddToCardPropertiesType = isVariationOnPLP(componentType)
        ? skuData
        : (mergedProductData as AddToCardPropertiesType)
    return getAddToCartProps(data, componentType)
}

export const getAddToCartProps = (
    data: AddToCardPropertiesType,
    componentType: string
): AddToCartPropsType => {
    const commonProps: AddToCartPropsType = {
        sku: data.sku,
        longSku: data.longSku,
        category_name: data.category_name,
        price: data.price ?? 0,
        strikethrough_price: Number(data.strikethrough_price),
        hidePrice: data.hidePrice,
        technologies: data.technologies || [],
        url: data.url || ''
    }

    if (isVariationOnPLP(componentType)) {
        return {
            ...commonProps,
            type: data.type,
            price_per_capsule: data.price_per_capsule,
            show_capsule_price: data.show_capsule_price,
            label_capsules: data.label_capsules,
            capsule_price_label: data.capsule_price_label,
            capsule_price_syntax: data.capsule_price_syntax,
            show_sleeve_price: data.show_sleeve_price,
            show_sleeve: data.show_sleeve,
            sleeve_syntax: data.sleeve_syntax,
            number_of_sleeves: data.number_of_sleeves,
            label_sleeves: data.label_sleeves,
            label_sleeve: data.label_sleeve,
            technologies: data.technologies || [],
            sales_multiple: data.sales_multiple,
            unit_quantity: data.unitQuantity,
            bundled: data.bundled,
            url: data.url || ''
        }
    }

    return commonProps
}

export const getPlpQuickViewProps = (
    popinId: string,
    skuData: SkuType,
    mergedProductData: MergedProductType,
    addToCartProps: AddToCartPropsType
): QuickViewPropsType => {
    return {
        popin: {
            popin_id: popinId,
            close: skuData?.quick_view_plp?.popin?.close,
            footer: true
        },
        product: {
            ...mergedProductData
        },
        intensity: {
            intensity_label: mergedProductData?.intensity_label,
            a11y_intensity_max: 'max of {max_intensity}'
        },
        cup_sizes: {
            ...skuData?.quick_view_plp?.cup_sizes,
            items: skuData?.cupSizesDetails
        },
        aromatic_profile: {
            ...skuData?.quick_view_plp?.aromatic_profile,
            items: skuData?.capsuleProductAromatics || mergedProductData?.capsuleProductAromatics
        },
        link: {
            ...skuData?.quick_view_plp?.link,
            url: skuData?.url
        },
        levels: {
            ...skuData?.quick_view_plp?.levels
        },
        pricing: {
            add_to_cart: {
                ...addToCartProps,
                position: '',
                variation: 'small',
                url: '',
                view_details_label: 'View details',
                rendererName: ''
            },
            showSleeve: {},
            showCapsulePrice: {}
        },
        campaign: skuData.campaign
    }
}

export const getCrossSellQuickViewProps = (
    popinId: string,
    product: ProductNewPropsType,
    skuData: SkuType,
    mergedProductData: MergedProductType,
    addToCartProps: AddToCartPropsType
): QuickViewPropsType => {
    const props: QuickViewPropsType = {
        popin: {
            popin_id: popinId,
            close: skuData?.copywriting?.sku_quick_view?.popin?.close,
            footer: true
        },
        product: {
            ...mergedProductData
        },
        intensity: {
            intensity_label: skuData?.options?.intensity_label,
            a11y_intensity_max: 'max of {max_intensity}'
        },
        cup_sizes: {
            ...skuData?.copywriting?.sku_quick_view?.cup_sizes,
            items: product?.cupSizesDetails
        },
        aromatic_profile: {
            ...skuData?.copywriting?.sku_quick_view?.aromatic_profile,
            items: product?.capsuleProductAromatics || mergedProductData?.capsuleProductAromatics
        },
        link: {
            ...skuData?.copywriting?.sku_quick_view?.link,
            url: mergedProductData?.pdpURLs?.desktop
        },
        levels: {
            ...skuData?.quick_view_plp?.levels
        },
        pricing: {
            add_to_cart: {
                ...addToCartProps,
                position: '',
                variation: 'small',
                url: '',
                view_details_label: 'View details',
                rendererName: ''
            },
            showSleeve: {},
            showCapsulePrice: {}
        },
        campaign: {}
    }
    return props
}
const isVariationOnPLP = (componentType: string) => componentType === VARIATION_PLP
